import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.setAttribute('crossOrigin', 'anonymous');
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop, targetWidth, targetHeight, fileType) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  // Use rounded integer values to prevent sub-pixel antialiasing and blurring
  const x = Math.round(pixelCrop.x);
  const y = Math.round(pixelCrop.y);
  const width = Math.round(pixelCrop.width);
  const height = Math.round(pixelCrop.height);

  const targetW = Math.round(targetWidth || width);
  const targetH = Math.round(targetHeight || height);

  // We start with a source canvas representing the cropped area at native quality
  let srcCanvas = document.createElement('canvas');
  srcCanvas.width = width;
  srcCanvas.height = height;
  const srcCtx = srcCanvas.getContext('2d');
  if (!srcCtx) {
    return null;
  }
  srcCtx.drawImage(image, x, y, width, height, 0, 0, width, height);

  let curWidth = width;
  let curHeight = height;

  // Multi-step downscaling to preserve image quality and avoid aliasing blur
  if (curWidth > targetW && curHeight > targetH) {
    while (curWidth * 0.5 > targetW && curHeight * 0.5 > targetH) {
      const nextCanvas = document.createElement('canvas');
      const nextWidth = Math.round(curWidth * 0.5);
      const nextHeight = Math.round(curHeight * 0.5);
      nextCanvas.width = nextWidth;
      nextCanvas.height = nextHeight;
      const nextCtx = nextCanvas.getContext('2d');
      if (nextCtx) {
        nextCtx.imageSmoothingEnabled = true;
        nextCtx.imageSmoothingQuality = 'high';
        nextCtx.drawImage(srcCanvas, 0, 0, curWidth, curHeight, 0, 0, nextWidth, nextHeight);
        srcCanvas = nextCanvas;
        curWidth = nextWidth;
        curHeight = nextHeight;
      } else {
        break;
      }
    }
  }

  canvas.width = targetW;
  canvas.height = targetH;

  // Set high quality smoothing settings on the final target canvas
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    srcCanvas,
    0,
    0,
    curWidth,
    curHeight,
    0,
    0,
    targetW,
    targetH
  );

  return new Promise((resolve, reject) => {
    let type = fileType || "image/png";
    let ext = type === "image/jpeg" ? "jpg" : "png";
    if (!fileType) {
      if (imageSrc.startsWith("data:image/jpeg") || imageSrc.match(/\.jpe?g$/i)) {
        type = "image/jpeg";
        ext = "jpg";
      }
    }

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(new File([blob], `cropped-image.${ext}`, { type: type }));
    }, type, 1.0);
  });
}

export default function ImageCropperModal({ imageSrc, onCropComplete, onCancel, cropWidth = 546, cropHeight = 350, fileType }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropCompleteInternal = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    try {
      if (!croppedAreaPixels) return;
      const file = await getCroppedImg(imageSrc, croppedAreaPixels, cropWidth, cropHeight, fileType);
      if (file) {
        onCropComplete(file);
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (!imageSrc) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: '#fff', padding: 20, borderRadius: 8, 
        width: '90%', maxWidth: '850px', height: '80%', maxHeight: '650px', 
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        <h4 className="mb-3">Crop Image ({cropWidth} x {cropHeight})</h4>
        
        <div style={{ position: 'relative', flex: 1, backgroundColor: '#333', borderRadius: 4, overflow: 'hidden' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={cropWidth / cropHeight}
            objectFit="contain"
            onCropChange={setCrop}
            onCropComplete={onCropCompleteInternal}
            onZoomChange={setZoom}
          />
        </div>
        
        <div className="mt-3 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center" style={{ flex: 1, marginRight: '20px' }}>
            <span className="me-2" style={{ fontWeight: 'bold' }}>Zoom:</span>
            <input 
              type="range" 
              value={zoom} 
              min={1} 
              max={3} 
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e) => setZoom(parseFloat(e.target.value))} 
              style={{ width: '100%' }}
            />
          </div>
          <div className="text-end" style={{ flexShrink: 0 }}>
            <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleCrop}>Crop & Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
